import { Stack, styled, Typography } from '@mui/material'
import Switch from '@mui/material/Switch'
import clsx from 'clsx'
import { ActionButton } from 'components/Buttons/ActionButton'
import { useConnectZkex } from 'hooks/useConnectZkex'
import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store'
import { useZkexConnecting } from 'store/account/hooks'
import { useLinkConnected, useLinkConnecting } from 'store/link/hooks'
import { updateSaveToken } from 'store/settings/actions'
import { useSaveToken } from 'store/settings/hooks'
import authorization from 'utils/authorization'
import restore from 'utils/restore'
import { useModal } from '../../store/app/hooks'
import { ContentWrap, Flex, Space24 } from '../../styles'
import Iconfont from '../Iconfont'
import IconHelp from '../IconHelp'
import Loading from '../Loading'
import ModalLink from '../ModalLink'
import { updateModal } from '../../store/app/actions/modalActions'
import { useWeb3Connected } from '../../store/web3/hooks'
import { useDisconnect } from '../../hooks/web3/useDisconnect'
import { throttle } from 'lodash'
import { IconInfo } from 'components/Icons'
import { ReactComponent as IconConnect } from './assets/connect.svg'

const Directions = styled(Flex)`
  gap: 8px;
  align-items: center;
  color: rgba(199, 255, 92, 1);
  padding: 8px 16px;
  background-color: rgba(160, 238, 9, 0.1);
  border-radius: 4px;

  span {
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
  }
`
const Step = styled(Flex)`
  margin-top: 24px;
  flex-direction: column;
  gap: 24px;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`
const StepItem = styled(Stack)`
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
  .iconfont {
    color: ${(props) => props.theme.color?.text80};
  }
`
const StepIcon = styled(Stack)`
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 33px;
  height: 33px;
  border-radius: 50%;
  background-color: rgba(58, 80, 25, 1);
  color: rgba(160, 238, 9, 1);
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  position: relative;

  &.active {
    background-color: ${(props) => props.theme.color?.text60};
    color: ${(props) => props.theme.color?.text00};
  }

  .line {
    content: '';
    width: 1px;
    height: 80px;
    position: absolute;
    top: 100%;
    left: 50%;
    background-color: rgba(72, 81, 88, 1);
  }
`
const StepText = styled(Stack)`
  margin-left: 18px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: rgba(255, 255, 255, 1);
  p {
    margin: 0;
  }
  small {
    color: rgba(149, 164, 173, 1);
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }
`
const SubmitWrap = styled(Flex)`
  gap: 8px;
  margin-top: 16px;
  justify-content: space-between;
  padding: 0 0 8px;
`
const Remember = styled(Stack)`
  color: rgba(202, 213, 220, 1);
  font-size: 14px;
  margin-top: 12px;
`
const ConnectButton = styled(ActionButton)`
  width: 150px;
  height: 40px;
  border-radius: 20px;
  font-size: 16px;
  gap: 8px;
`
const BackButton = styled(ActionButton)`
  width: 220px;
  height: 40px;
  border-radius: 20px;
  background-color: rgba(57, 65, 73, 1);
  font-size: 16px;
  color: rgba(202, 213, 220, 1);
  &:hover {
    color: rgba(160, 238, 9, 1);
    background-color: rgba(57, 65, 73, 1);
  }
`

export const RecoverKeysModal = memo(() => {
  const dispatch = useAppDispatch()
  const isIn = useModal('verify')
  const { t } = useTranslation()
  const isSaveToken = useSaveToken()
  const connectZkex = useConnectZkex()
  const web3Connected = useWeb3Connected()
  const linkConnecting = useLinkConnecting()
  const linkConnected = useLinkConnected()
  const zkexConnecting = useZkexConnecting()
  const disconnect = useDisconnect()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    if (!checked) {
      restore.clean()
      authorization.clean()
    }
    dispatch(updateSaveToken(checked))
  }

  const onHandleConnect = throttle(connectZkex, 1000)

  const renderConnectButton = () => {
    if (linkConnecting) {
      return (
        <ConnectButton variant="contained" size="small">
          <Loading color="inherit" /> <span>Connecting</span>
        </ConnectButton>
      )
    }
    if (zkexConnecting) {
      return (
        <ConnectButton variant="contained" size="small">
          <Loading color="inherit" /> <span>Processing</span>
        </ConnectButton>
      )
    }
    if (linkConnected) {
      return (
        <ConnectButton
          variant="contained"
          size="small"
          onClick={onHandleConnect}
        >
          <IconConnect />
          <span>Connect</span>
        </ConnectButton>
      )
    }
    if (web3Connected) {
      return (
        <ConnectButton
          variant="contained"
          size="small"
          onClick={onHandleConnect}
        >
          <IconConnect /> <span>Connect</span>
        </ConnectButton>
      )
    }
    return (
      <ConnectButton variant="contained" size="small" onClick={onHandleConnect}>
        <Iconfont name="icon-refresh" size={16} />
        <span>{t('link-wallet-try-again')}</span>
      </ConnectButton>
    )
  }

  return (
    <ModalLink
      width="435px"
      isIn={isIn}
      header={t('link-wallet-title')}
      onClose={() => dispatch(updateModal({ modal: 'verify', open: false }))}
    >
      <ContentWrap>
        <Directions>
          <IconInfo style={{ width: '24px', height: '24px' }} />
          <Stack>
            <span>{t('link-wallet-receive')}</span>
            <span>{t('link-wallet-transaction')}</span>
          </Stack>
        </Directions>
        <Step>
          <StepItem>
            <StepIcon className={clsx({ active: linkConnected })}>
              {!linkConnected ? (
                '1'
              ) : (
                <Iconfont name="icon-check-fill" size={16}></Iconfont>
              )}
              <i className="line" />
            </StepIcon>
            <StepText>
              <p>{t('link-wallet-verify')}</p>
              <p>
                <small>{t('link-wallet-confirm')}</small>
              </p>
            </StepText>
          </StepItem>
          <StepItem>
            <StepIcon>2</StepIcon>
            <StepText>
              <p>{t('link-wallet-connection')}</p>
              <p>
                <small>{t('link-wallet-initiating')}</small>
              </p>
            </StepText>
          </StepItem>
        </Step>
        <Remember
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography variant="body2">Remember me (12 hr)</Typography>
            <IconHelp title={t('description-remember-me') ?? ''} />
          </Stack>

          <Switch checked={isSaveToken} onChange={handleChange} />
        </Remember>
        <SubmitWrap>
          <BackButton
            onClick={() => {
              disconnect()
              dispatch(updateModal({ modal: 'wallets', open: true }))
            }}
          >
            Back to wallet selection
          </BackButton>
          {renderConnectButton()}
        </SubmitWrap>
      </ContentWrap>
    </ModalLink>
  )
})
